import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownRight, ArrowUpRight } from "@digital-twins/design-system/icons";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { intlLocaleForUi } from "@/i18n";
import { cn } from "@/lib/utils";
import { SocialTopicDelta } from "./charts/socialTopicMetrics";
import { useTweenedNumber } from "./useTweenedNumber";

/**
 * A stat figure: grouped below 10,000, compact above it ("4.7M", "158.1K"),
 * like the prototype. Exported for the other Topic panels (and re-exported
 * from `SocialTopicKpis`, where it used to live).
 */
export function formatTopicStat(value: number, uiLanguage: string): string {
  const locale = intlLocaleForUi(uiLanguage);
  if (Math.abs(value) >= 10000) {
    return new Intl.NumberFormat(locale, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }
  return new Intl.NumberFormat(locale).format(value);
}

export interface SocialStatProps {
  id: string;
  label: string;
  value: number | null;
  /** Formats the (tweened) value. Defaults to `formatTopicStat` in the UI language. */
  format?: (value: number) => string;
  /** Test ids are `${testIdPrefix}-${id}`, `-value` and `-note`. */
  testIdPrefix?: string;
  /** Shown for a null value. Defaults to the Topic page's n/a. */
  notAvailableLabel?: string;
  delta?: ReactNode;
  note?: ReactNode;
  /** A small trend line under the value (`SocialSparkline`). */
  spark?: ReactNode;
}

interface SocialSparklineProps {
  /** Oldest first. Under 2 values, or all zero, draws nothing. */
  values: Array<number>;
  testId?: string;
}

/** A tiny trend line with a soft fill, in the primary chart token. Decorative: the figure carries the number. */
export function SocialSparkline(props: SocialSparklineProps) {
  const { values, testId } = props;

  const max = Math.max(0, ...values);
  if (values.length < 2 || max <= 0) {
    return null;
  }
  const width = 96;
  const height = 22;
  const step = width / (values.length - 1);
  const points = values.map((value, index) => {
    const x = index * step;
    const y = height - 1 - (value / max) * (height - 3);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const line = `M${points.join(" L")}`;
  const area = `${line} L${width},${height} L0,${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="mt-1 h-[22px] w-24 overflow-visible"
      aria-hidden="true"
      data-testid={testId}
    >
      <path d={area} className="fill-chart-primary/15" />
      <path
        d={line}
        className="fill-none stroke-chart-primary"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** One figure of a stat row: a muted label, the value with an optional delta, a note. */
export function SocialStat(props: SocialStatProps) {
  const {
    id,
    label,
    value,
    format,
    testIdPrefix = "social-topic-kpi",
    notAvailableLabel,
    delta,
    note,
    spark,
  } = props;

  const { t, i18n } = useTranslation("app");
  const shown = useTweenedNumber(value);

  const formatValue = format ?? ((next: number) => formatTopicStat(next, i18n.language));

  return (
    <div className="flex min-w-0 flex-col gap-0.5" data-testid={`${testIdPrefix}-${id}`}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="flex items-baseline gap-2 whitespace-nowrap">
        <span
          className="text-[22px] font-semibold leading-tight tracking-[-0.02em] tabular-nums text-foreground"
          data-testid={`${testIdPrefix}-${id}-value`}
        >
          {shown == null
            ? (notAvailableLabel ?? t("socialListening.topicPage.stats.notAvailable"))
            : formatValue(shown)}
        </span>
        {delta}
      </span>
      {spark}
      <span
        className="min-h-[18px] text-xs text-muted-foreground tabular-nums"
        data-testid={`${testIdPrefix}-${id}-note`}
      >
        {note}
      </span>
    </div>
  );
}

export interface SocialStatDeltaProps {
  id: string;
  delta: SocialTopicDelta | null;
  /** The aria label; the caller builds it (the Topic page: "Up {value} since {date}"). */
  label: string;
  /** The test id is `${testIdPrefix}-${id}-delta`. */
  testIdPrefix?: string;
}

/** "↗ +12", underlined. The comparison ("since Sep 14") is the hover tip, not the chip. */
export function SocialStatDelta(props: SocialStatDeltaProps) {
  const { id, delta, label, testIdPrefix = "social-topic-kpi" } = props;

  const { i18n } = useTranslation("app");

  if (!delta) {
    return null;
  }
  const magnitude = formatTopicStat(Math.abs(delta.change), i18n.language);
  const sign = delta.direction === "up" ? "+" : delta.direction === "down" ? "-" : "";
  const Icon = delta.direction === "down" ? ArrowDownRight : ArrowUpRight;
  const figure = (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
        delta.direction === "up" && "text-chart-positive",
        delta.direction === "down" && "text-chart-negative",
        delta.direction === "flat" && "text-muted-foreground",
      )}
      aria-label={label}
      data-direction={delta.direction}
      data-testid={`${testIdPrefix}-${id}-delta`}
    >
      {delta.direction === "flat" ? null : <Icon className="h-3 w-3" aria-hidden="true" />}
      <span
        aria-hidden="true"
        className={label ? "cursor-help underline decoration-dotted underline-offset-2" : undefined}
      >
        {sign}
        {magnitude}
      </span>
    </span>
  );

  if (!label) {
    return figure;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        {/* asChild keeps the trigger the span that carries the test id. */}
        <TooltipTrigger asChild>{figure}</TooltipTrigger>
        <TooltipContent className="text-xs" data-testid={`${testIdPrefix}-${id}-delta-tip`}>
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface SocialMeasureSwitchOption<T extends string> {
  value: T;
  label: string;
}

export interface SocialMeasureSwitchProps<T extends string> {
  options: ReadonlyArray<SocialMeasureSwitchOption<T>>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  /** Each option's test id is `${testIdPrefix}-${option.value}`. */
  testIdPrefix: string;
}

/** A small pill switch (the Topic page's Reach / Posts, a collection's window). */
export function SocialMeasureSwitch<T extends string>(props: SocialMeasureSwitchProps<T>) {
  const { options, value, onChange, ariaLabel, testIdPrefix } = props;

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex rounded-full border border-border p-0.5"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          className={cn(
            "h-[26px] rounded-full px-2.5 text-xs font-medium transition-colors duration-150",
            value === option.value
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground",
          )}
          data-testid={`${testIdPrefix}-${option.value}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
